import React, { Component } from 'react'
import { Form, Page, Box, Icons } from 'components'
import { getLocationState, getMatchParams, navigate } from 'shared/router'
import { ShippingParcelCreateRequest, TmrPlace, TmrZone } from 'api/types'
import { showToast, uuid } from 'shared/utils'
import Zones from 'api/Zones'
import { T, __ } from 'translations/i18n'
import RemoteConfig, { InboundConfig } from 'shared/RemoteConfig'
import InboundShipments from 'api/InboundShipments'
import Places from 'api/Places'

interface State {
  zones?: TmrZone[]
  places?: TmrPlace[]
  initialValues?: any
  fromShipment: boolean
  loading: boolean
}
export default class InboundCreate extends Component<{}, State> {
  operation = RemoteConfig.getOperationConfig<InboundConfig>(getMatchParams(this.props).configCode)

  state: State = {
    zones: [],
    places: [],
    fromShipment: getLocationState(this.props)?.fromShipment,
    initialValues: {
      destination: getLocationState(this.props)?.destination,
      shipmentCode: getLocationState(this.props)?.shipmentCode,
    },
    loading: true,
  }

  componentDidMount() {
    this.fetchZones()
  }

  fetchZones = async () => {
    const { initialValues } = this.state
    try {
      const zones = ((await Zones.search<TmrZone>({ limit: 500 })) ?? []).filter((z) => z.zoneType === 'STOCK')
      const places = (await Places.search<TmrPlace>({ limit: 500 })) ?? []
      if (zones.length === 1 && !initialValues.destination) {
        initialValues.destination = zones[0]
      }
      this.setState({ zones, places, initialValues, loading: false })
    } catch (err) {
      showToast({
        title: __(T.error.error),
        description: err?.message ?? 'Generic error',
        status: 'error',
      })
    }
  }

  submit = async (data) => {
    const { fromShipment } = this.state
    if (!data.destination) {
      showToast({
        title: __(T.error.error),
        description: __(T.error.destination_field_required),
        status: 'error',
      })
      return
    }

    let state = {
      shipmentCode: data.shipmentCode ?? uuid(),
      parcelCode: data.parcelCode ?? uuid(),
      destination: data.destination,
      fromShipment,
    }
    try {
      if (this.operation?.id) {
        const request: ShippingParcelCreateRequest = {
          configurationId: this.operation.id,
          destinationPlaceCode: data.destination.code,
          checklistType: 'ITEMS',
        }
        if (data.shipmentCode) request.shippingCode = data.shipmentCode
        if (data.parcelCode) request.parcelCode = data.parcelCode
        if (data.origin) request.originPlaceCode = data.origin.code

        const response = await InboundShipments.createParcel(request)
        if (!response || !response?.header?.parcelCode) throw new Error('No Parcel created') // Never shown to user

        state = {
          shipmentCode: response?.header?.shippingCode,
          parcelCode: response?.header?.parcelCode,
          destination: data.destination,
          fromShipment,
        }
        navigate(
          '/inbound/:configCode/reading/:parcelCode',
          { parcelCode: state.parcelCode, configCode: this.operation?.code },
          { state }
        )
      }
    } catch (error) {
      showToast({ status: 'error', title: error.message })
    }
  }

  onBackPress = () => {
    if (this.state.fromShipment) {
      navigate(`/inbound/:configCode/:shippingCode/parcels`, {
        configCode: this.operation?.code,
        shippingCode: this.state.initialValues?.shipmentCode,
      })
      return
    }
    if (this.operation.hasChecklist === 'yes' || this.operation.inboundMode === 'shipment') {
      navigate(`/inbound/:configCode`, { configCode: this.operation?.code })
    } else {
      navigate(`/`)
    }
  }

  render() {
    const { zones, places, initialValues, loading } = this.state

    return (
      <Page title={this.operation?.description ?? 'Inbound'} onBackPress={this.onBackPress}>
        <Page.Content bgGrey>
          <Box flex center loading={loading}>
            <Form
              schema={[
                {
                  label: __(T.misc.shipment_code_auto),
                  name: 'shipmentCode',
                  image: <Icons.Barcode />,
                  focus: !initialValues.shipmentCode,
                  hide: this.state.fromShipment,
                },
                {
                  label: __(T.misc.parcel_code_auto),
                  name: 'parcelCode',
                  image: <Icons.Barcode />,
                  focus: initialValues.shipmentCode,
                },
                {
                  placeholder: __(T.misc.destination),
                  name: 'destination',
                  required: true,
                  type: 'select',
                  options: zones,
                  config: { value: 'id', label: 'description', secondaryLabel: 'code' },
                  hide: this.state.fromShipment,
                },
                {
                  placeholder: __(T.misc.origin),
                  name: 'origin',
                  required: true,
                  type: 'select',
                  options: places,
                  config: { value: 'id', label: 'description', secondaryLabel: 'code' },
                  disabled: this.state.fromShipment,
                  hide: this.state.fromShipment,
                },
              ]}
              initialValues={initialValues}
              submitText={__(T.misc.next)}
              onSubmit={this.submit}
              width={400}
            />
          </Box>
        </Page.Content>
      </Page>
    )
  }
}
